import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  ChannelDetailCopywriting,
  ChannelType,
} from '@/components/widget/ChannelDetail';
import { WidgetViewState } from '@/components/widget/WidgetOpeningManager';
import { ChannelOptions } from '@/types/widgetConfig';
import { useWidgetConfig } from './useWidgetConfig';
import { useWidgetUIStore } from './useWidgetUIStore';

/**
 * Channel information interface
 */
export interface ChannelInfo {
  name: string;
  type: ChannelType;
  displayName: string;
  icon?: string;
}

export const DISABLE_CHANNEL_DETAIL_CHANNELS = new Set(['line']);

/**
 * Get channel information including translated name and display name
 * Uses translation key format: channels.{channelType}
 *
 * @param channelType - The type of channel (whatsapp, facebook, etc.)
 * @param channelData - The channel configuration data
 * @param t - Translation function
 * @returns Channel information object
 */
export function getChannelInfo(
  channelType: ChannelType,
  channelData: Record<string, unknown> | null,
  t: TFunction,
): ChannelInfo {
  const channelName =
    channelType.charAt(0).toUpperCase() + channelType.slice(1);

  // Get translated channel name and icon using specific channel mapping
  const channelMapping = (() => {
    switch (channelType) {
      case 'whatsapp':
        return {
          name: t('channels.whatsapp', 'WhatsApp'),
        };
      case 'facebook':
        return {
          name: t('channels.facebook', 'Facebook'),
        };
      case 'instagram':
        return {
          name: t('channels.instagram', 'Instagram'),
        };
      case 'wechat':
        return {
          name: t('channels.wechat', 'WeChat'),
        };
      case 'line':
        return {
          name: t('channels.line', 'LINE'),
        };
      case 'telegram':
        return {
          name: t('channels.telegram', 'Telegram'),
        };
      case 'viber':
        return {
          name: t('channels.viber', 'Viber'),
        };
      default:
        return {
          name: channelName,
        };
    }
  })();

  return {
    name: channelMapping.name,
    type: channelType,
    displayName: String(
      (channelData as Record<string, unknown>)?.channelDisplayName ||
        (channelData as Record<string, unknown>)?.name ||
        channelMapping.name,
    ),
  };
}

/**
 * Generate channel URL for QR code based on channel type and configuration
 *
 * @param channelType - The type of channel
 * @param channelData - The channel configuration data
 * @returns Generated URL string or empty string if invalid
 */
export function getChannelUrl(
  channelType: ChannelType,
  channelData: Record<string, unknown> | null,
): string {
  if (!channelData) return '';

  switch (channelType) {
    case 'whatsapp': {
      // WhatsApp URL format: https://wa.me/{phoneNumber}
      const phoneNumber =
        channelData?.whatsappPhoneNumber ||
        channelData?.facebookDisplayPhoneNumber;
      return phoneNumber
        ? `https://wa.me/${String(phoneNumber).replace(/[^0-9]/g, '')}`
        : '';
    }

    case 'facebook': {
      // Facebook Messenger URL format
      const pageId = channelData?.pageId;
      return pageId ? `https://m.me/${pageId}` : '';
    }

    case 'instagram': {
      // Instagram URL format
      const instagramId = channelData?.name;
      return instagramId ? `https://ig.me/m/${instagramId}` : '';
    }

    case 'line': {
      // LINE URL format
      const basicId = channelData?.basicId;
      return basicId ? `https://line.me/R/ti/p/${basicId}` : '';
    }

    case 'telegram': {
      // Telegram URL format
      const telegramDeeplink = channelData?.telegramDeeplink;
      return String(telegramDeeplink);
    }

    case 'viber': {
      // Viber URL format
      const viberDeeplink = channelData?.viberDeeplink;
      return String(viberDeeplink);
    }

    default:
      return '';
  }
}

export function getQRCodeImage(
  channelType: ChannelType,
  channelData: Record<string, unknown> | null,
) {
  if (channelType === 'wechat') {
    return (channelData as Record<string, string>)?.qrCodeURL;
  }

  return '';
}

/**
 * Get copywriting configuration with proper translation keys
 * Uses specific translation keys for each channel
 *
 * @param channelType - The type of channel
 * @param channelInfo - Channel information object
 * @param t - Translation function
 * @returns Copywriting configuration object
 */
export function getCopywriting(
  channelType: ChannelType,
  channelInfo: ChannelInfo,
  t: TFunction,
): Required<ChannelDetailCopywriting> {
  const channelName = channelInfo.name;
  // Apply custom copywriting overrides if provided
  return {
    title: t('channelDetail.common.title', {
      defaultValue: 'Chat with us on {{channelName}}',
      channelName,
    }),
    description: t('channelDetail.common.description', {
      defaultValue:
        'Scan the QR code to start chatting with us on {{channelName}}',
      channelName,
    }),
    continueButtonText: t('channelDetail.common.continueButtonText', {
      defaultValue: 'Continue on {{channelName}}',
      channelName,
    }),
    continueButtonDescription: t(
      'channelDetail.common.continueButtonDescription',
      {
        defaultValue: 'Click the button below to open {{channelName}} directly',
        channelName,
      },
    ),
  };
}

interface UseChannelOption {
  channelType?: keyof ChannelOptions | null;
}
const useChannels = ({ channelType }: UseChannelOption = {}) => {
  const { t } = useTranslation();
  const { channelConfig } = useWidgetConfig();
  const { actions } = useWidgetUIStore();

  // Get current channel data
  const channelData = useMemo(() => {
    if (!channelType) return null;
    return channelConfig.options?.[channelType];
  }, [channelType, channelConfig.options]);

  // Get channel information using utility function
  const channelInfo = useMemo(() => {
    if (!channelType) return null;
    return getChannelInfo(channelType, channelData || null, t);
  }, [channelType, channelData, t]);

  // Generate channel URL using utility function
  const channelUrl = useMemo(() => {
    if (!channelType) return '';
    return getChannelUrl(channelType, channelData || null);
  }, [channelType, channelData]);

  const qrCodeImage = useMemo(() => {
    if (!channelType) return '';
    return getQRCodeImage(channelType, channelData || null);
  }, [channelType, channelData]);

  // Get copywriting using utility function
  const copywriting = useMemo(() => {
    if (!channelType || !channelInfo) {
      return {
        title: '',
        description: '',
        continueButtonText: '',
        continueButtonDescription: '',
      };
    }
    return getCopywriting(channelType, channelInfo, t);
  }, [channelType, channelInfo, t]);

  const selectChannel = useCallback(
    (channel: keyof ChannelOptions) => {
      const channelUrl = getChannelUrl(
        channel,
        channelConfig.options?.[channel] ?? null,
      );
      const qrCodeImage = getQRCodeImage(
        channel,
        channelConfig.options?.[channel] ?? null,
      );

      if (!channelUrl && !qrCodeImage) return;

      if (DISABLE_CHANNEL_DETAIL_CHANNELS.has(channel)) {
        window.open(channelUrl, '_blank', 'noopener,noreferrer');
      } else {
        actions.setCurrentView(WidgetViewState.CHANNEL_DETAIL);
        actions.setChannelDetailType(channel);
      }
    },
    [actions, channelConfig.options],
  );

  const isLiveChatAvailable = useMemo(() => {
    return channelConfig.availableChannels.livechat;
  }, [channelConfig.availableChannels]);

  const isOtherChannelsAvailable = useMemo(() => {
    return Object.entries(channelConfig.availableChannels).some(
      ([channel, value]) => value && channel !== 'livechat',
    );
  }, [channelConfig.availableChannels]);

  return {
    channelData,
    channelInfo,
    channelUrl,
    qrCodeImage,
    copywriting,
    selectChannel,
    isLiveChatAvailable,
    isOtherChannelsAvailable,
  };
};

export default useChannels;
